import type {
  CurrencyPrice,
  EditPricelistResponse,
  FeeOfferingDto,
  PriceBandRow,
  PricePeriods,
} from "app/shared/model/EditPricelist";
import { normIso } from "./normalizeCurrency";
import { isFeeOffering, isNonFeeOffering } from "../offering/guards";

const normalizeIsos = (isos: string[]): string[] =>
  Array.from(new Set(isos.map((iso) => normIso(iso)).filter(Boolean)));

export const getEnabledCurrencies = (
  pricelist: EditPricelistResponse[],
  centreCurrencyIso?: string
): string[] => {
  const set = new Set<string>();
  const add = (iso?: string) => {
    const code = normIso(iso);
    if (code) set.add(code);
  };

  add(centreCurrencyIso);

  (pricelist ?? [])
    .forEach((offering: any) => {
    if (isNonFeeOffering(offering)) {
      (offering.pricePeriods ?? []).forEach((period: PricePeriods) => {
        (period.priceBands ?? []).forEach((band: PriceBandRow) => {
          (band.prices ?? []).forEach((price: CurrencyPrice) =>
            add(price.currencyIsoCode)
          );
        });
      });
      }

      if (isFeeOffering(offering)) {
        const fee = offering as FeeOfferingDto;
        (fee.oneOffFeePrices ?? []).forEach((p: any) => add(p?.currencyIsoCode));
        (fee.supplementPeriods ?? []).forEach((period: any) => {
          (period?.prices ?? []).forEach((p: any) => add(p?.currencyIsoCode));
        });
      }
    });

  const centre = normIso(centreCurrencyIso);
  const all = [...set.values()].sort((a, b) => a.localeCompare(b));
  if (centre && all.includes(centre)) {
    return [centre, ...all.filter((c) => c !== centre)];
  }
  return all;
};

export const syncCurrenciesInAllBands = (
    pricelist: EditPricelistResponse[],
    currencyIsos: string[],
    selectedPricingYear?: number
): EditPricelistResponse[] => {
  const normalizedIsos = normalizeIsos(currencyIsos);
  if (!normalizedIsos.length) return pricelist ?? [];

  const targetYear = Number(selectedPricingYear);

  return (pricelist ?? []).map((offering: any) => {
    if (isNonFeeOffering(offering)) {
      let updated = false;
      const pricePeriods = (offering.pricePeriods ?? []).map((period: any) => {
        const priceBands = (period.priceBands ?? []).map((band: any) => {
          const prices = Array.isArray(band.prices) ? [...band.prices] : [];
          const filtered = prices.filter((p: any) =>
              normalizedIsos.includes(normIso(p?.currencyIsoCode))
          );
          if (filtered.length !== prices.length) updated = true;

          const existing = new Set(
              filtered.map((p: any) => normIso(p?.currencyIsoCode)).filter(Boolean)
          );

          normalizedIsos.forEach((iso) => {
            if (!existing.has(iso)) {
              filtered.push({
                currencyIsoCode: iso,
                pricePerMajorUnit: 0,
                pricePerMinorUnit: 0,
              });
              updated = true;
            }
          });

          return { ...band, prices: filtered };
        });

        return { ...period, priceBands };
      });

      if (!updated) return offering;
      return { ...offering, pricePeriods, isUpdated: true };
    }

    if (!isFeeOffering(offering)) return offering;

    const fee = { ...offering } as FeeOfferingDto;
    let updated = false;

    const oneOff = Array.isArray(fee.oneOffFeePrices) ? [...fee.oneOffFeePrices] : [];
    const filteredOneOff = oneOff.filter((p: any) =>
        normalizedIsos.includes(normIso(p?.currencyIsoCode))
    );
    if (filteredOneOff.length !== oneOff.length) updated = true;

    if (Number.isFinite(targetYear)) {
      const yearRows = filteredOneOff.filter((x: any) => Number(x.year) === targetYear);
      const existingOneOff = new Set(yearRows.map((x: any) => normIso(x.currencyIsoCode)));
      normalizedIsos.forEach((iso) => {
        if (!existingOneOff.has(iso)) {
          filteredOneOff.push({ year: targetYear, currencyIsoCode: iso, amount: 0 });
          updated = true;
        }
      });
    }

    const supplementPeriods = (fee.supplementPeriods ?? []).map((period: any) => {
      const periodYear = Number(period?.year ?? targetYear ?? 0);
      const periodPrices = Array.isArray(period?.prices) ? [...period.prices] : [];

      const filtered = periodPrices.filter((p: any) =>
          normalizedIsos.includes(normIso(p?.currencyIsoCode))
      );
      if (filtered.length !== periodPrices.length) updated = true;

      const existing = new Set(filtered.map((p: any) => normIso(p?.currencyIsoCode)));
      normalizedIsos.forEach((iso) => {
        if (!existing.has(iso)) {
          filtered.push({
            currencyIsoCode: iso,
            pricePerMajorUnit: 0,
            pricePerMinorUnit: 0,
          });
          updated = true;
        }
      });

      return {
        ...period,
        year: periodYear,
        prices: filtered,
      };
    });

    if (!updated) return offering;
    return {
      ...fee,
      oneOffFeePrices: filteredOneOff,
      supplementPeriods,
      isUpdated: true,
    };
  });
};